import { useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Option } from '@material-tailwind/react';
import Input from '@components/forms/Input';
import Button from '@components/forms/Button';
import Select from '@components/forms/Select';
import { useAuthStore } from '@stores';
import { extractDomain } from '@utils';
import { REGISTER_BROKER_ORG, post } from '@services';

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const CREDIT_AUTH_TYPES = [
  { value: 'ACL_HOLDER', label: 'I hold an Australian Credit Licence' },
  { value: 'CREDIT_REPRESENTATIVE', label: 'I am a credit representative of an Australian Credit Licence Holder' },
] as const;

type CreditAuthorisationType = typeof CREDIT_AUTH_TYPES[number]['value'];

const schema = yup.object({
  organisationName: yup.string().required('Organisation name is required.'),
  abn: yup
    .string()
    .required('ABN is required.')
    .matches(/^\d{2}\s\d{3}\s\d{3}\s\d{3}$|^\d{11}$/, 'Enter a valid 11-digit ABN.'),
  street: yup.string().required('Street address is required.'),
  suburb: yup.string().required('Suburb is required.'),
  state: yup.string().required('State is required.'),
  postcode: yup
    .string()
    .required('Postcode is required.')
    .matches(/^\d{4}$/, 'Enter a valid 4-digit postcode.'),
  creditAuthorisationType: yup
    .string()
    .oneOf(['ACL_HOLDER', 'CREDIT_REPRESENTATIVE'] as const)
    .required('Please select how you are authorised to provide credit assistance.'),
  aclNumber: yup.string().when('creditAuthorisationType', {
    is: 'ACL_HOLDER',
    then: (s) => s.required('Australian Credit Licence number is required.'),
    otherwise: (s) => s.optional(),
  }),
  creditRepresentativeNumber: yup.string().when('creditAuthorisationType', {
    is: 'CREDIT_REPRESENTATIVE',
    then: (s) => s.required('Credit representative number is required.'),
    otherwise: (s) => s.optional(),
  }),
  licenseeName: yup.string().when('creditAuthorisationType', {
    is: 'CREDIT_REPRESENTATIVE',
    then: (s) => s.required('Licensee name is required.'),
    otherwise: (s) => s.optional(),
  }),
  licenseeAclNumber: yup.string().when('creditAuthorisationType', {
    is: 'CREDIT_REPRESENTATIVE',
    then: (s) => s.required('Licensee ACL number is required.'),
    otherwise: (s) => s.optional(),
  }),
  declaration: yup
    .boolean()
    .required()
    .oneOf([true], 'You must confirm the declaration before submitting.'),
  declarationVerification: yup
    .boolean()
    .required()
    .oneOf([true], 'You must acknowledge the verification statement before submitting.'),
}).required();

type RegisterFormData = yup.InferType<typeof schema>;
type RegisterField = keyof RegisterFormData;

const API_ERROR_FIELDS: Record<string, RegisterField | 'emailDomain'> = {
  InvalidAbn: 'abn',
  InvalidBroker: 'emailDomain',
};

const fieldFromApiMessage = (message: string): RegisterField | 'emailDomain' | undefined => {
  const text = message.toLowerCase();
  if (text.includes('abn')) return 'abn';
  if (text.includes('email domain')) return 'emailDomain';
  if (text.includes('licensee acl')) return 'licenseeAclNumber';
  if (text.includes('licensee name')) return 'licenseeName';
  if (text.includes('acr')) return 'creditRepresentativeNumber';
  if (text.includes('acl')) return 'aclNumber';
  if (text.includes('post code') || text.includes('postcode')) return 'postcode';
  if (text.includes('suburb')) return 'suburb';
  if (text.includes('state')) return 'state';
  if (text.includes('street') || text.includes('address')) return 'street';
  if (text.includes('organisation') || text.includes('name')) return 'organisationName';
  if (text.includes('declaration')) return 'declaration';
  return undefined;
};

const readApiError = (error: unknown) => {
  const data = (
    error as {
      response?: {
        data?: {
          error?: { code?: string; message?: string };
          errors?: { code?: string; message?: string }[];
        };
      };
    }
  )?.response?.data;
  const body = data?.error || data?.errors?.[0];

  return {
    code: body?.code,
    message: body?.message || 'Unable to register your organisation. Please try again.',
  };
};

const BrokerOrgRegister = () => {
  const navigate = useNavigate();
  const { brokerOrgStatus, setBrokerOrgStatus } = useAuthStore();
  const [submitError, setSubmitError] = useState('');
  const [domainError, setDomainError] = useState('');
  const email = localStorage.getItem('email') || '';
  const domain = extractDomain(email);

  useEffect(() => {
    const { canAccessBrokerOrg, isBroker } = useAuthStore.getState();
    if (!isBroker()) {
      navigate('/', { replace: true });
      return;
    }
    if (brokerOrgStatus === 'PENDING' || brokerOrgStatus === 'ACTIVE') {
      navigate(canAccessBrokerOrg() ? '/broker-org' : '/', { replace: true });
    }
  }, [brokerOrgStatus, navigate]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema),
  });

  const creditAuthorisationType = useWatch({ control, name: 'creditAuthorisationType' }) as CreditAuthorisationType | undefined;

  const onSubmit = async (data: RegisterFormData) => {
    setSubmitError('');
    setDomainError('');
    clearErrors();
    const isLicenceHolder = data.creditAuthorisationType === 'ACL_HOLDER';
    const emailDomain = domain.toLowerCase();

    try {
      await post(
        REGISTER_BROKER_ORG,
        {
          name: data.organisationName,
          abn: data.abn.replace(/\s/g, ''),
          ...(emailDomain ? { emailDomain } : {}),
          address: {
            addressLine: data.street,
            suburb: data.suburb,
            state: data.state,
            postCode: data.postcode,
          },
          creditLicence: isLicenceHolder
            ? {
                type: 'LICENCE_HOLDER',
                aclNumber: data.aclNumber,
              }
            : {
                type: 'CREDIT_REPRESENTATIVE',
                acrNumber: data.creditRepresentativeNumber,
                licenseeName: data.licenseeName,
                licenseeAclNumber: data.licenseeAclNumber,
              },
          hasDeclaration: true,
        },
        false
      );
      setBrokerOrgStatus('PENDING');
      // Replace so browser Back from /broker-org does not land on this form, which
      // would redirect straight back to /broker-org and reload it.
      navigate('/broker-org', { replace: true });
    } catch (error) {
      const { code, message } = readApiError(error);
      const field = (code && API_ERROR_FIELDS[code]) || fieldFromApiMessage(message);

      if (field === 'emailDomain') {
        setDomainError(message);
        return;
      }
      if (field) {
        setError(field, { type: 'server', message });
        setFocus(field);
        return;
      }
      setSubmitError(message);
    }
  };

  return (
    <div className="py-6 px-4 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-grey-900 mb-1">Register your Broker Organisation</h1>
      <p className="text-sm text-grey-600 mb-8">
        Your details will be reviewed before your organisation is activated. This usually takes 1–2 business days.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <p className="text-xs font-bold text-grey-400 uppercase tracking-wider mb-4 pb-2 border-b border-grey-200">
            Organisation Details
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm font-medium text-grey-700 mb-1.5">Organisation Name *</p>
              <Input
                {...register('organisationName')}
                error={!!errors.organisationName}
                placeholder="e.g. Valeur Finance Group"
              />
              {errors.organisationName && (
                <span className="text-error-500 text-xs mt-1 block">{errors.organisationName.message}</span>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-1.5">ABN *</p>
              <Input {...register('abn')} error={!!errors.abn} placeholder="XX XXX XXX XXX" />
              {errors.abn && (
                <span className="text-error-500 text-xs mt-1 block">{errors.abn.message}</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-grey-700 mb-1.5">
              Email Domain
              <span className="ml-2 bg-grey-100 text-grey-500 text-xs font-medium px-2 py-0.5 rounded">
                Auto-filled
              </span>
            </p>
            <div className="flex items-center">
              <span className={`bg-grey-100 border border-r-0 rounded-l-md px-3 h-10 flex items-center text-sm text-grey-500 ${domainError ? 'border-error-500' : 'border-grey-300'}`}>
                @
              </span>
              <div className={`flex-1 bg-grey-50 border rounded-r-md h-10 flex items-center px-3 text-sm font-medium text-grey-700 ${domainError ? 'border-error-500' : 'border-grey-300'}`}>
                {domain || <span className="text-grey-400">no email found</span>}
                <span className="ml-auto text-grey-400">🔒</span>
              </div>
            </div>
            {domainError && (
              <span className="text-error-500 text-xs mt-1 block">{domainError}</span>
            )}
            <p className="text-xs text-grey-500 mt-1">
              Derived from your registered email <strong>{email}</strong>. All org members must share this domain.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <p className="text-xs font-bold text-grey-400 uppercase tracking-wider mb-4 pb-2 border-b border-grey-200">
            Registered Address
          </p>
          <div className="mb-4">
            <p className="text-sm font-medium text-grey-700 mb-1.5">Street Address *</p>
            <Input {...register('street')} error={!!errors.street} placeholder="e.g. 123 Collins Street" />
            {errors.street && (
              <span className="text-error-500 text-xs mt-1 block">{errors.street.message}</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-medium text-grey-700 mb-1.5">Suburb / City *</p>
              <Input {...register('suburb')} error={!!errors.suburb} placeholder="e.g. Melbourne" />
              {errors.suburb && (
                <span className="text-error-500 text-xs mt-1 block">{errors.suburb.message}</span>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-1.5">State *</p>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    error={!!errors.state}
                    onChange={(val) => field.onChange(val)}>
                    {AU_STATES.map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Select>
                )}
              />
              {errors.state && (
                <span className="text-error-500 text-xs mt-1 block">{errors.state.message}</span>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-1.5">Postcode *</p>
              <Input {...register('postcode')} error={!!errors.postcode} placeholder="e.g. 3000" maxLength={4} />
              {errors.postcode && (
                <span className="text-error-500 text-xs mt-1 block">{errors.postcode.message}</span>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <p className="text-xs font-bold text-grey-400 uppercase tracking-wider mb-1 pb-2 border-b border-grey-200">
            Credit Authority
          </p>
          <p className="text-xs text-grey-500 mb-4">
            This helps us verify that you are authorised to arrange or assist with vehicle or asset finance.
          </p>

          <div className="mb-4">
            <p className="text-sm font-medium text-grey-700 mb-2">
              How are you authorised to provide credit assistance? *
            </p>
            <div className="flex flex-col gap-2">
              {CREDIT_AUTH_TYPES.map(({ value, label }) => (
                <label
                  key={value}
                  className="flex items-start gap-3 p-3 rounded-lg border border-grey-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                  <input
                    type="radio"
                    value={value}
                    {...register('creditAuthorisationType')}
                    className="mt-0.5 accent-blue-600 shrink-0"
                  />
                  <span className="text-sm text-grey-800">{label}</span>
                </label>
              ))}
            </div>
            {errors.creditAuthorisationType && (
              <span className="text-error-500 text-xs mt-1 block">{errors.creditAuthorisationType.message}</span>
            )}
          </div>

          {creditAuthorisationType === 'ACL_HOLDER' && (
            <div className="mb-4">
              <p className="text-sm font-medium text-grey-700 mb-1.5">Australian Credit Licence Number *</p>
              <Input {...register('aclNumber')} error={!!errors.aclNumber} placeholder="e.g. 123456" />
              {errors.aclNumber && (
                <span className="text-error-500 text-xs mt-1 block">{errors.aclNumber.message}</span>
              )}
            </div>
          )}

          {creditAuthorisationType === 'CREDIT_REPRESENTATIVE' && (
            <>
              <div className="mb-4">
                <p className="text-sm font-medium text-grey-700 mb-1.5">Your Credit Representative Number *</p>
                <Input
                  {...register('creditRepresentativeNumber')}
                  error={!!errors.creditRepresentativeNumber}
                  placeholder="e.g. 123456"
                />
                {errors.creditRepresentativeNumber && (
                  <span className="text-error-500 text-xs mt-1 block">{errors.creditRepresentativeNumber.message}</span>
                )}
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium text-grey-700 mb-1.5">Licensee Name *</p>
                <Input
                  {...register('licenseeName')}
                  error={!!errors.licenseeName}
                  placeholder="Name of the ACL holder"
                />
                {errors.licenseeName && (
                  <span className="text-error-500 text-xs mt-1 block">{errors.licenseeName.message}</span>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-grey-700 mb-1.5">Licensee ACL Number *</p>
                <Input
                  {...register('licenseeAclNumber')}
                  error={!!errors.licenseeAclNumber}
                  placeholder="e.g. 123456"
                />
                {errors.licenseeAclNumber && (
                  <span className="text-error-500 text-xs mt-1 block">{errors.licenseeAclNumber.message}</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <p className="text-xs font-bold text-grey-400 uppercase tracking-wider mb-1 pb-2 border-b border-grey-200">
            Declaration
          </p>
          <p className="text-xs text-grey-500 mb-4">
            Please confirm the following before submitting.
          </p>
          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('declaration')}
                className="mt-0.5 accent-blue-600 shrink-0 w-4 h-4"
              />
              <span className="text-sm text-grey-800">
                I confirm that the information provided is accurate and that I am authorised to act as a finance broker in Australia.
              </span>
            </label>
            {errors.declaration && (
              <span className="text-error-500 text-xs -mt-1 block">{errors.declaration.message}</span>
            )}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('declarationVerification')}
                className="mt-0.5 accent-blue-600 shrink-0 w-4 h-4"
              />
              <span className="text-sm text-grey-800">
                I understand Authentik8 may verify my details before enabling direct inspection purchasing.
              </span>
            </label>
            {errors.declarationVerification && (
              <span className="text-error-500 text-xs -mt-1 block">{errors.declarationVerification.message}</span>
            )}
          </div>
        </div>

        {submitError && (
          <p className="text-error-500 text-sm mb-4">{submitError}</p>
        )}

        <div className="flex justify-end gap-3 pt-6 border-t border-grey-200">
          <Button
            type="button"
            variant="outlined"
            className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 bg-white"
            onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-secondary text-white normal-case font-semibold rounded-lg text-sm py-2 px-4 capitalize hover:bg-secondary-dark disabled:opacity-50">
            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BrokerOrgRegister;
